import { useState } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  Button,
} from 'konsta/react'
import {
  Activity,
  Server,
  Database,
  HardDrive,
  Wifi,
  Wallet,
  ListTodo,
  Cpu,
  MemoryStick,
  CircleGauge,
  Users,
  RefreshCw,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
} from 'lucide-react'
import { cn, formatRelativeTime } from '@/lib/utils'

// --- Types ---

type ServiceStatus = 'healthy' | 'degraded' | 'down'

interface Service {
  name: string
  status: ServiceStatus
  latency: string
  uptime: string
  icon: React.ElementType
}

interface Incident {
  id: string
  service: string
  description: string
  status: 'resolved' | 'investigating' | 'monitoring'
  date: Date
}

// --- Mock Data ---

const mockServices: Service[] = [
  { name: 'API Server', status: 'healthy', latency: '12ms', uptime: '99.99%', icon: Server },
  { name: 'PostgreSQL', status: 'healthy', latency: '3ms', uptime: '99.98%', icon: Database },
  { name: 'Redis Cache', status: 'healthy', latency: '1ms', uptime: '100%', icon: HardDrive },
  { name: 'WebSocket', status: 'healthy', latency: '8ms', uptime: '99.97%', icon: Wifi },
  { name: 'Wallet API', status: 'degraded', latency: '450ms', uptime: '99.5%', icon: Wallet },
  { name: 'Job Queue', status: 'healthy', latency: '5ms', uptime: '99.99%', icon: ListTodo },
]

const mockPerformance = [
  { label: 'CPU', value: 23, icon: Cpu, color: '#34C759' },
  { label: 'Memory', value: 67, icon: MemoryStick, color: '#FF9500' },
  { label: 'Disk', value: 45, icon: CircleGauge, color: '#007AFF' },
  { label: '活躍連線', value: 127, icon: Users, color: '#AF52DE', isCount: true },
]

const mockIncidents: Incident[] = [
  {
    id: '1',
    service: 'Wallet API',
    description: 'Wallet API 回應時間增加至 450ms，正在監控中',
    status: 'monitoring',
    date: new Date(Date.now() - 1000 * 60 * 45),
  },
  {
    id: '2',
    service: 'Redis Cache',
    description: 'Redis 連線池耗盡導致短暫中斷，已修復',
    status: 'resolved',
    date: new Date(Date.now() - 1000 * 60 * 60 * 6),
  },
  {
    id: '3',
    service: 'WebSocket',
    description: '部分用戶 WebSocket 斷線，已自動恢復',
    status: 'resolved',
    date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
  },
]

// --- Helpers ---

function getStatusConfig(status: ServiceStatus) {
  switch (status) {
    case 'healthy':
      return { label: '正常', color: '#34C759', bgClass: 'bg-[#34C759]/15' }
    case 'degraded':
      return { label: '延遲', color: '#FF9500', bgClass: 'bg-[#FF9500]/15' }
    case 'down':
      return { label: '故障', color: '#FF3B30', bgClass: 'bg-[#FF3B30]/15' }
  }
}

function getIncidentStatusConfig(status: Incident['status']) {
  switch (status) {
    case 'resolved':
      return { label: '已解決', color: '#34C759', Icon: CheckCircle2 }
    case 'investigating':
      return { label: '調查中', color: '#FF3B30', Icon: AlertTriangle }
    case 'monitoring':
      return { label: '監控中', color: '#FF9500', Icon: Activity }
  }
}

function getBarColor(value: number) {
  if (value < 50) return '#34C759'
  if (value < 80) return '#FF9500'
  return '#FF3B30'
}

// --- Component ---

export function AdminHealthScreen() {
  const [refreshing, setRefreshing] = useState(false)

  const healthyCount = mockServices.filter((s) => s.status === 'healthy').length
  const totalCount = mockServices.length
  const overallStatus = mockServices.every((s) => s.status === 'healthy')
    ? 'healthy'
    : mockServices.some((s) => s.status === 'down')
      ? 'down'
      : 'degraded'

  const overallConfig = getStatusConfig(overallStatus)

  const handleRefresh = () => {
    setRefreshing(true)
    setTimeout(() => setRefreshing(false), 1500)
  }

  return (
    <>
      <Navbar title="系統健康" />

      {/* Overall Status */}
      <Block className="!px-4 !mt-2 !mb-0">
        <div className="rounded-2xl bg-white p-5 shadow-sm border border-black/5 text-center">
          <div
            className={cn(
              'w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3',
              overallConfig.bgClass,
            )}
          >
            {overallStatus === 'healthy' ? (
              <CheckCircle2 className="w-8 h-8" style={{ color: overallConfig.color }} />
            ) : overallStatus === 'degraded' ? (
              <AlertTriangle className="w-8 h-8" style={{ color: overallConfig.color }} />
            ) : (
              <XCircle className="w-8 h-8" style={{ color: overallConfig.color }} />
            )}
          </div>
          <p className="text-lg font-bold" style={{ color: overallConfig.color }}>
            系統{overallConfig.label}
          </p>
          <p className="text-sm text-[#8E8E93] mt-1">
            {healthyCount}/{totalCount} 服務正常運行
          </p>
          <Button
            small
            tonal
            rounded
            className="!mt-3 !inline-flex"
            onClick={handleRefresh}
          >
            <RefreshCw
              className={cn('w-3.5 h-3.5 mr-1', refreshing && 'animate-spin')}
            />
            {refreshing ? '刷新中...' : '刷新狀態'}
          </Button>
        </div>
      </Block>

      {/* Services List */}
      <BlockTitle>服務狀態</BlockTitle>
      <List strongIos insetIos>
        {mockServices.map((service) => {
          const config = getStatusConfig(service.status)
          const Icon = service.icon
          return (
            <ListItem
              key={service.name}
              media={
                <div
                  className={cn(
                    'w-10 h-10 rounded-xl flex items-center justify-center',
                    config.bgClass,
                  )}
                >
                  <Icon className="w-5 h-5" style={{ color: config.color }} />
                </div>
              }
              title={
                <span className="font-medium">{service.name}</span>
              }
              subtitle={
                <span className="text-[#8E8E93] text-[12px]">
                  延遲: {service.latency} &middot; 可用率: {service.uptime}
                </span>
              }
              after={
                <span
                  className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold',
                    config.bgClass,
                  )}
                  style={{ color: config.color }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full mr-1"
                    style={{ backgroundColor: config.color }}
                  />
                  {config.label}
                </span>
              }
            />
          )
        })}
      </List>

      {/* Performance Metrics */}
      <BlockTitle>效能指標</BlockTitle>
      <Block className="!px-4">
        <div className="grid grid-cols-2 gap-3">
          {mockPerformance.map((metric) => {
            const Icon = metric.icon
            const barColor = metric.isCount ? metric.color : getBarColor(metric.value)
            return (
              <div
                key={metric.label}
                className="rounded-2xl bg-white p-4 shadow-sm border border-black/5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${metric.color}15` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: metric.color }} />
                  </div>
                  <span className="text-sm font-medium text-[#8E8E93]">{metric.label}</span>
                </div>
                <p className="text-2xl font-bold text-black">
                  {metric.isCount ? metric.value : `${metric.value}%`}
                </p>
                {!metric.isCount && (
                  <div className="w-full h-2 bg-[#F2F2F7] rounded-full mt-2 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${metric.value}%`,
                        backgroundColor: barColor,
                      }}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </Block>

      {/* Recent Incidents */}
      <BlockTitle className="flex items-center justify-between !pr-4">
        <span>近期事件</span>
        <span className="text-xs font-medium text-[#8E8E93]">
          {mockIncidents.length} 筆
        </span>
      </BlockTitle>
      <List strongIos insetIos>
        {mockIncidents.map((incident) => {
          const config = getIncidentStatusConfig(incident.status)
          const StatusIcon = config.Icon
          return (
            <ListItem
              key={incident.id}
              media={
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${config.color}15` }}
                >
                  <StatusIcon className="w-5 h-5" style={{ color: config.color }} />
                </div>
              }
              title={
                <span className="font-medium text-sm">{incident.service}</span>
              }
              subtitle={
                <div>
                  <p className="text-[#8E8E93] text-[12px] leading-tight mt-0.5">
                    {incident.description}
                  </p>
                  <span className="flex items-center gap-1 text-[#8E8E93] text-[11px] mt-1">
                    <Clock className="w-3 h-3" />
                    {formatRelativeTime(incident.date)}
                  </span>
                </div>
              }
              after={
                <span
                  className="text-[11px] font-medium"
                  style={{ color: config.color }}
                >
                  {config.label}
                </span>
              }
            />
          )
        })}
      </List>

      {/* Bottom padding */}
      <div className="h-8" />
    </>
  )
}
